import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from "gsap";
import { cursosAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AppSidebar } from '../components/AppSidebar';
import { CourseCard } from '../components/CourseCard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  BookOpen, 
  TrendingUp, 
  Award, 
  Play,
  Users,
  Clock,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Grid3X3,
  List
} from 'lucide-react';

function MeusCursos() {
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();
  const containerRef = useRef(null);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingMatricula, setLoadingMatricula] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, progress, title
  const [filterBy, setFilterBy] = useState('all'); // all, em-progresso, concluidos
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  useEffect(() => {
    if (!authLoading && usuario) {
      carregarMeusCursos();
    }
  }, [authLoading, usuario]);

  useEffect(() => {
    if (!loading && cursos.length > 0) {
      const ctx = gsap.context(() => {
        // Verificar e animar elementos da página
        const fadeInElements = containerRef.current?.querySelectorAll(".fade-in")
        if (fadeInElements && fadeInElements.length > 0) {
          gsap.fromTo(
            fadeInElements,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: 0.1,
              ease: "power3.out",
              delay: 0.2,
            },
          );
        }

        // Verificar e animar estatísticas
        const statCards = containerRef.current?.querySelectorAll(".stat-card")
        if (statCards && statCards.length > 0) {
          gsap.fromTo(
            statCards,
            { opacity: 0, scale: 0.9 },
            {
              opacity: 1,
              scale: 1,
              duration: 0.6,
              stagger: 0.1,
              ease: "power3.out",
              delay: 0.4,
            },
          );
        }
      }, containerRef);

      return () => ctx.revert();
    }
  }, [loading, cursos]);

  const carregarMeusCursos = async () => {
    try {
      setLoading(true);
      const response = await cursosAPI.meusCursos();
      setCursos(response.data || []);
    } catch (err) {
      setError('Erro ao carregar seus cursos');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMatricula = async (cursoId, isMatriculado) => {
    try {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: true }));
      
      if (isMatriculado) {
        await cursosAPI.desmatricular(cursoId);
        // Remover curso da lista já que ele não fará mais parte dos "meus cursos"
        setCursos(prev => prev.filter(curso => curso.id !== cursoId));
      }
    } catch (err) {
      console.error('Erro na operação:', err);
      alert(err.response?.data?.erro || 'Erro ao realizar operação');
    } finally {
      setLoadingMatricula(prev => ({ ...prev, [cursoId]: false }));
    }
  };

  const handleVerDetalhes = (cursoId) => {
    navigate(`/curso/${cursoId}`);
  };

  const handleContinuarAssistindo = (cursoId) => {
    navigate(`/assistir-curso/${cursoId}`);
  };

  const handleGerarCertificado = async (cursoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/certificados/gerar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ curso_id: cursoId })
      });

      const data = await response.json();

      if (response.ok) {
        // Sucesso - mostrar mensagem e redirecionar ou baixar certificado
        alert(`Certificado gerado com sucesso! Credencial: ${data.credential_id}`);
        
        // Opcional: abrir o PDF diretamente
        if (data.pdf_url) {
          window.open(`http://localhost:5000${data.pdf_url}`, '_blank');
        }

        // Recarregar cursos para atualizar o estado dos botões
        await carregarMeusCursos();

        // Opcional: redirecionar para a página de certificados
        // navigate('/meus-certificados');
      } else {
        throw new Error(data.erro || 'Erro ao gerar certificado');
      }
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
      alert(error.message || 'Erro ao gerar certificado');
    }
  };

  const handleVerCertificado = (certificado) => {
    if (certificado?.pdf_path) {
      window.open(`http://localhost:5000/uploads/${certificado.pdf_path}`, '_blank');
    } else if (certificado?.credential_id) {
      // Navegar para página de validação
      window.open(`/certificados/${certificado.credential_id}`, '_blank');
    }
  };

  // Verificar se usuário está autenticado
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-white text-lg">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    navigate('/auth');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-white text-lg">Carregando seus cursos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <AppSidebar />
        <div className="ml-64 min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Erro ao carregar cursos</h2>
            <p className="text-slate-400">{error}</p>
            <Button onClick={carregarMeusCursos} className="bg-red-600 hover:bg-red-700">
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Filtrar e ordenar cursos
  const cursosFiltrados = cursos.filter(curso => {
    const matchSearch = curso.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       curso.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       curso.categoria_nome?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchSearch) return false;
    
    if (filterBy === 'em-progresso') {
      return curso.progresso && curso.progresso.porcentagem > 0 && curso.progresso.porcentagem < 100;
    } else if (filterBy === 'concluidos') {
      return curso.progresso && curso.progresso.porcentagem === 100;
    }
    
    return true;
  });

  const cursosOrdenados = [...cursosFiltrados].sort((a, b) => {
    switch (sortBy) {
      case 'progress':
        return (b.progresso?.porcentagem || 0) - (a.progresso?.porcentagem || 0);
      case 'title':
        return a.titulo.localeCompare(b.titulo);
      case 'recent':
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });

  // Calcular estatísticas
  const totalCursos = cursos.length;
  const cursosComProgresso = cursos.filter(c => c.progresso && c.progresso.porcentagem > 0);
  const cursosConcluidos = cursos.filter(c => c.progresso && c.progresso.porcentagem === 100);
  const progressoMedio = cursos.length > 0 
    ? Math.round(cursos.reduce((acc, curso) => acc + (curso.progresso?.porcentagem || 0), 0) / cursos.length)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <AppSidebar />
      <div ref={containerRef} className="ml-64 min-h-screen">
        {/* Header */}
        <div className="fade-in border-b border-slate-800/50 bg-slate-950/50 backdrop-blur-xl sticky top-0 z-40">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent mb-2">
                  Meus Cursos
                </h1>
                <p className="text-slate-400 text-lg">
                  Acompanhe seu progresso e continue aprendendo
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
                >
                  {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="stat-card bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{totalCursos}</p>
                    <p className="text-sm text-slate-400">Total de Cursos</p>
                  </div>
                </div>
              </div>

              <div className="stat-card bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{cursosConcluidos.length}</p>
                    <p className="text-sm text-slate-400">Concluídos</p>
                  </div>
                </div>
              </div>

              <div className="stat-card bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-600/20 rounded-lg flex items-center justify-center">
                    <Play className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{cursosComProgresso.length}</p>
                    <p className="text-sm text-slate-400">Em Progresso</p>
                  </div>
                </div>
              </div>

              <div className="stat-card bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{progressoMedio}%</p>
                    <p className="text-sm text-slate-400">Progresso Médio</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros e Busca */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Busca */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar nos seus cursos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent backdrop-blur-sm"
                />
              </div>

              {/* Filtros */}
              <div className="flex gap-2">
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 backdrop-blur-sm"
                >
                  <option value="all">Todos os Cursos</option>
                  <option value="em-progresso">Em Progresso</option>
                  <option value="concluidos">Concluídos</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 backdrop-blur-sm"
                >
                  <option value="recent">Mais Recentes</option>
                  <option value="progress">Maior Progresso</option>
                  <option value="title">Nome A-Z</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="container mx-auto px-6 py-8">
          {cursosOrdenados.length === 0 ? (
            <div className="text-center py-24 space-y-6">
              {searchTerm || filterBy !== 'all' ? (
                <>
                  <div className="text-6xl mb-8">🔍</div>
                  <h3 className="text-3xl font-bold text-white mb-4">Nenhum curso encontrado</h3>
                  <p className="text-xl text-slate-400 max-w-md mx-auto leading-relaxed">
                    Não encontramos cursos que correspondam aos seus filtros de busca.
                  </p>
                  <Button 
                    onClick={() => {
                      setSearchTerm('');
                      setFilterBy('all');
                    }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Limpar Filtros
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-6xl mb-8">📚</div>
                  <h3 className="text-3xl font-bold text-white mb-4">Você ainda não se matriculou em nenhum curso</h3>
                  <p className="text-xl text-slate-400 max-w-md mx-auto leading-relaxed">
                    Explore nossa biblioteca de cursos e comece sua jornada de aprendizado!
                  </p>
                  <Button 
                    onClick={() => navigate('/')}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Explorar Cursos
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Informações dos resultados */}
              <div className="fade-in">
                <p className="text-slate-400">
                  Mostrando {cursosOrdenados.length} de {totalCursos} cursos
                  {searchTerm && ` para "${searchTerm}"`}
                </p>
              </div>

              {/* Grid de cursos */}
              <div className={viewMode === 'grid' 
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
                : "space-y-6"
              }>
                {cursosOrdenados.map((course, index) => (
                  <div key={course.id} className="fade-in">
                    <CourseCard 
                      course={course} 
                      delay={index * 0.1}
                      onMatricula={handleMatricula}
                      onVerDetalhes={handleVerDetalhes}
                      onGerarCertificado={handleGerarCertificado}
                      onVerCertificado={handleVerCertificado}
                      loadingMatricula={loadingMatricula[course.id]}
                      showProgress={true}
                    />
                  </div>
                ))}
              </div>

              {/* Seção de ações rápidas */}
              {cursosComProgresso.length > 0 && (
                <div className="fade-in mt-16 p-6 bg-slate-900/30 backdrop-blur-xl border border-slate-800/50 rounded-xl">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Play className="w-5 h-5 text-red-400" />
                    Continue de onde parou
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cursosComProgresso.slice(0, 3).map((curso) => (
                      <div 
                        key={curso.id}
                        className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/30 hover:border-red-500/30 transition-colors cursor-pointer"
                        onClick={() => handleContinuarAssistindo(curso.id)}
                      >
                        <h4 className="font-semibold text-white mb-2 line-clamp-1">{curso.titulo}</h4>
                        <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                          <span>{Math.round(curso.progresso?.porcentagem || 0)}% concluído</span>
                          <span>{curso.progresso?.videos_concluidos || 0}/{curso.progresso?.total_videos || 0} vídeos</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${curso.progresso?.porcentagem || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MeusCursos; 